import { Injectable } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestDefinition } from '../../shared/model/request-definition';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DetailedRequestDefinition } from '../../shared/model/detailed-request-definition';

import { saveAs } from 'file-saver';
import { FileInfo } from '../../shared/model/file-info';
import {Task} from '../model/task';
import moment from 'moment';
import {RequestDefinitionAttributes} from '../model/request-definition-attributes';

@Injectable()
export class RequestDefinitionService {

  constructor(public _http: HttpClient) {
  }


  /**
   * Get a list of all request definitions
   * @returns {Observable<RequestDefinition[]>}
   */
  getRequestDefinitions(): Observable<RequestDefinition[]> {
    return new Observable<RequestDefinition[]>((subscriber: Subscriber<RequestDefinition[]>) => {
      this._http
        .get(environment.endpoints.clientBaseUrl + environment.endpoints.requestDefinitionList)
/*        .pipe(
          map((rd: RequestDefinition[]) => {
            for (let i = 0; i < rd.length; i++) {
              rd[i].lastRunDate = new Date(rd[i].lastRunDate);
            }
            return rd;
          })
        )*/
        .subscribe(
          (response: RequestDefinition[]) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Get a list of all available request definitions
   * @returns {Observable<RequestDefinition[]>}
   */
  getAvailableRequestDefinitions(): Observable<RequestDefinition[]> {
    // return of([
    //   {
    //     id: 0,
    //     name: 'Dummy Requestdefinition',
    //     description: 'Example description',
    //     version: '1',
    //     status: 'ok',
    //     lastRunDate: new Date(),
    //     initiatorClient: 'arzdarmstadt',
    //     emailNotification: null
    //   }
    // ]);
    return this._http.get<RequestDefinition[]>(environment.endpoints.clientBaseUrl + environment.endpoints.getAvailableRequestDefinitions);
  }

  /**
   * Get a single request definition by it's ID
   * @param {number} id
   */
  getRequestDefinitionById(id: number) {
    return new Observable<DetailedRequestDefinition>((subscriber: Subscriber<DetailedRequestDefinition>) => {
      this._http
        .get(environment.endpoints.clientBaseUrl + environment.endpoints.getRequestDefinition.replace('{id}', String(id)))
        .pipe(
          map((rd: DetailedRequestDefinition) => {
            rd.validFrom = moment(rd.validFrom).toDate();
            rd.validUntil = moment(rd.validUntil).toDate();
            return rd;
          })
        )
        .subscribe(
          (requestDefinition: DetailedRequestDefinition) => {
            subscriber.next(requestDefinition);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Create a new request definition
   * @param {DetailedRequestDefinition} requestDefinition The new request definition
   * @returns {Observable<any>}
   */
  createRequestDefinition(requestDefinition: DetailedRequestDefinition): Observable<any> {
    return new Observable<any>((subscriber: Subscriber<any>) => {
      this._http
        .post(environment.endpoints.clientBaseUrl + environment.endpoints.createRequestDefinition, requestDefinition)
        .pipe(
          map((rd: DetailedRequestDefinition) => {
            rd.validFrom = new Date(rd.validFrom);
            rd.validUntil = new Date(rd.validUntil);
            return rd;
          })
        )
        .subscribe(
          (response: any) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Update an existing request definition
   * @param {DetailedRequestDefinition} requestDefinition The updated request definition
   * @returns {Observable<DetailedRequestDefinition>}
   */
  updateRequestDefinition(requestDefinition: DetailedRequestDefinition): Observable<DetailedRequestDefinition> {
    return new Observable<DetailedRequestDefinition>((subscriber: Subscriber<DetailedRequestDefinition>) => {
      this._http
        .put(environment.endpoints.clientBaseUrl + environment.endpoints.updateRequestDefinition, requestDefinition)
        .pipe(
          map((rd: DetailedRequestDefinition) => {
            rd.validFrom = new Date(rd.validFrom);
            rd.validUntil = new Date(rd.validUntil);
            return rd;
          })
        )
        .subscribe(
          (response: DetailedRequestDefinition) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Update an existing request definition
   * @param {DetailedRequestDefinition} requestDefinition The updated request definition
   * @returns {Observable<DetailedRequestDefinition>}
   */
  updateRequestDefinitionAttributes(requestDefinition: RequestDefinitionAttributes): Observable<DetailedRequestDefinition> {
    return new Observable<DetailedRequestDefinition>((subscriber: Subscriber<DetailedRequestDefinition>) => {
      this._http
        .put(environment.endpoints.clientBaseUrl + environment.endpoints.updateRequestDefinitionAttributes, requestDefinition)
        .pipe(
          map((rd: DetailedRequestDefinition) => {
            rd.validFrom = new Date(rd.validFrom);
            rd.validUntil = new Date(rd.validUntil);
            return rd;
          })
        )
        .subscribe(
          (response: DetailedRequestDefinition) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  _downloadDocumentationAsBlob(requestDefinitionId: number): Observable<FileInfo> {
    return new Observable<FileInfo>((subscriber: Subscriber<FileInfo>) => {
      this._http.get(environment.endpoints.clientBaseUrl + environment.endpoints.downloadDocumentation
        .replace('{rdId}', String(requestDefinitionId)), {responseType: 'blob', observe: 'response'})
        .subscribe(
          (response: HttpResponse<Blob>) => {
            // Get filename
            let fileName = response.headers.get('Content-Disposition');
            fileName = fileName.split('=')[1];

            subscriber.next({data: response.body, fileName: fileName});
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Download a {@link RequestDefinition}'s documentation
   * @param {number} requestDefinitionId The {@link RequestDefinition}'s ID
   * @returns {Observable<any>}
   */
  downloadDocumentation(requestDefinitionId: number): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      this._downloadDocumentationAsBlob(requestDefinitionId)
        .subscribe(
          (fileInfo: FileInfo) => {
            saveAs(fileInfo.data, fileInfo.fileName);
            subscriber.next(fileInfo);
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Accept a request definition
   * @param {number} requestDefinitionId The request definition's ID
   * @returns {Observable<RequestDefinition>} The approved request definition
   */
  acceptRequestDefinition(requestDefinitionId: number): Observable<RequestDefinition> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.approveRequestDefinition
      .replace('{id}', String(requestDefinitionId));

    return this._http.get<RequestDefinition>(url);
  }

  /**
   * Deny a request definition
   * @param {number} requestDefinitionId The request definition's ID
   * @returns {Observable<RequestDefinition>} The denied request definition
   */
  denyRequestDefinition(requestDefinitionId: number): Observable<RequestDefinition> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.denyRequestDefinition
      .replace('{id}', String(requestDefinitionId));

    return this._http.get<RequestDefinition>(url);
  }

  /**
   * Start a request definition
   * @param requestDefinitionId The ID of the request definition to start
   */
  startRequestDefinition(requestDefinitionId: number): Observable<DetailedRequestDefinition> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.startRequestDefinition
      .replace('{id}', String(requestDefinitionId));

    return this._http.get(url);
  }

  /**
   * Get all subtasks for an request request definition id
   * @param requestDefinitionId The ID of the request definition
   * @returns (Observable<Task[]>) List of subtasks
   */
  getRequestDefinitionSubtasks(requestDefinitionId: number): Observable<Task[]> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.getRequestDefinitionSubtasks
      .replace('{id}', String(requestDefinitionId));

    return new Observable<Task[]>((subscriber: Subscriber<Task[]>) => {
      this._http.get<Task[]>(url).subscribe(
          (response: Task[]) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

}
